import { AbsoluteFill, interpolate, useCurrentFrame } from "remotion";
import {
  Caption,
  Callout,
  CodePanel,
  COLORS,
  FONTS,
  MetricCounter,
  Panel,
  PipelineGraph,
  SceneTransition,
  SlideTimeBar,
  type PipeNode,
} from "../../coldcompute";
import { SCENE } from "./constants";
import {
  HOOK,
  PROBLEM,
  INTUITION,
  APPROACH,
  CODE,
  TIEBREAK,
  PAYOFF,
  OUTRO,
} from "./script";

const center: React.CSSProperties = {
  alignItems: "center",
  justifyContent: "center",
  flexDirection: "column",
};

const Kicker: React.FC<{ text: string; color: string }> = ({ text, color }) => (
  <div
    style={{
      fontFamily: FONTS.mono,
      fontSize: 22,
      letterSpacing: "0.22em",
      textTransform: "uppercase",
      color,
      marginBottom: 22,
    }}
  >
    ▍{text}
  </div>
);

const Big: React.FC<{ children: React.ReactNode; size?: number }> = ({
  children,
  size = 92,
}) => (
  <div
    style={{
      fontFamily: FONTS.display,
      fontWeight: 700,
      fontSize: size,
      lineHeight: 0.98,
      letterSpacing: "-0.02em",
      color: COLORS.text,
      textAlign: "center",
      maxWidth: 1500,
    }}
  >
    {children}
  </div>
);

/* 01 — HOOK */
export const Scene01Hook: React.FC = () => {
  const frame = useCurrentFrame();
  const swap = interpolate(frame, [44, 74], [0, 1], {
    extrapolateLeft: "clamp",
    extrapolateRight: "clamp",
  });
  return (
    <AbsoluteFill style={center}>
      <SlideTimeBar sceneDurationFrames={SCENE.hook.duration} />
      <SceneTransition from="up">
        <AbsoluteFill style={center}>
          <Kicker text="capability inference · the choice" color={COLORS.cyan} />
          <Big>
            intent → agent skills
            <br />
            <span style={{ fontSize: 64, color: COLORS.dim }}>
              <span
                style={{
                  color: COLORS.red,
                  textDecoration: "line-through",
                  opacity: 0.4 + swap * 0.6,
                }}
              >
                an LLM
              </span>{" "}
              <span style={{ color: COLORS.dim }}>·</span>{" "}
              <span style={{ color: COLORS.green, opacity: 0.3 + swap * 0.7 }}>
                a keyword matcher
              </span>
            </span>
          </Big>
        </AbsoluteFill>
      </SceneTransition>
      <Caption lines={HOOK} accent="cyan" />
    </AbsoluteFill>
  );
};

/* 02 — PROBLEM (why NOT an LLM on the hot path) */
export const Scene02Problem: React.FC = () => (
  <AbsoluteFill style={center}>
    <SlideTimeBar sceneDurationFrames={SCENE.problem.duration} />
    <SceneTransition from="up">
      <AbsoluteFill style={center}>
        <Kicker text="an LLM on every request costs you" color={COLORS.amber} />
        <div style={{ display: "flex", gap: 28, alignItems: "stretch" }}>
          <Panel accent="amber" title="cost · tokens" width={400}>
            <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", height: 150 }}>
              <div style={{ fontFamily: FONTS.display, fontWeight: 700, fontSize: 56, color: COLORS.amber }}>4–5</div>
              <div style={{ fontFamily: FONTS.mono, fontSize: 18, color: COLORS.dim, textAlign: "center" }}>LLM turns burned<br />to "discover" the obvious</div>
            </div>
          </Panel>
          <Panel accent="red" title="latency · hot path" width={400}>
            <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", height: 150 }}>
              <div style={{ fontFamily: FONTS.display, fontWeight: 700, fontSize: 56, color: COLORS.red }}>+1 RTT</div>
              <div style={{ fontFamily: FONTS.mono, fontSize: 18, color: COLORS.dim, textAlign: "center" }}>on EVERY request</div>
            </div>
          </Panel>
          <Panel accent="violet" title="non-determinism" width={400}>
            <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", height: 150 }}>
              <div style={{ fontFamily: FONTS.display, fontWeight: 700, fontSize: 56, color: COLORS.violet }}>≠</div>
              <div style={{ fontFamily: FONTS.mono, fontSize: 18, color: COLORS.dim, textAlign: "center" }}>same input,<br />different output</div>
            </div>
          </Panel>
        </div>
      </AbsoluteFill>
    </SceneTransition>
    <Caption lines={PROBLEM} accent="amber" />
  </AbsoluteFill>
);

/* 03 — INTUITION (the words already say it) */
export const Scene03Intuition: React.FC = () => (
  <AbsoluteFill style={center}>
    <SlideTimeBar sceneDurationFrames={SCENE.intuition.duration} />
    <SceneTransition from="up">
      <AbsoluteFill style={center}>
        <Kicker text="intuition" color={COLORS.violet} />
        <div
          style={{
            fontFamily: FONTS.mono,
            fontSize: 40,
            color: COLORS.text,
            background: COLORS.panel,
            border: `1px solid ${COLORS.line}`,
            borderRadius: 12,
            padding: "20px 34px",
            marginBottom: 34,
          }}
        >
          "send me a{" "}
          <span style={{ color: COLORS.lime }}>daily</span>{" "}
          <span style={{ color: COLORS.cyan }}>code-smell</span>{" "}
          <span style={{ color: COLORS.green }}>email</span>"
        </div>
        <div style={{ display: "flex", gap: 22 }}>
          <Callout label="outcome" accent="cyan" width={260}>
            <b style={{ color: COLORS.text }}>code smells</b> → read the KB
          </Callout>
          <Callout label="channel" accent="green" width={260}>
            <b style={{ color: COLORS.text }}>email</b> → send permission
          </Callout>
          <Callout label="cadence" accent="lime" width={260}>
            <b style={{ color: COLORS.text }}>daily</b> → a cron hint
          </Callout>
        </div>
      </AbsoluteFill>
    </SceneTransition>
    <Caption lines={INTUITION} accent="violet" />
  </AbsoluteFill>
);

/* 04 — APPROACH (pipeline) */
const PIPE: PipeNode[] = [
  { label: "intent", value: "free text", accent: "cyan" },
  { label: "lowercase", value: "substr test", sub: "vs KEYWORD_RULES", accent: "green" },
  { label: "capabilities", value: "slug set", sub: "sorted · deduped", accent: "lime" },
  { label: "confidence", value: "by count", sub: "3+ = high", accent: "amber" },
];
export const Scene04Approach: React.FC = () => (
  <AbsoluteFill style={center}>
    <SlideTimeBar sceneDurationFrames={SCENE.approach.duration} />
    <AbsoluteFill style={{ ...center, padding: "0 90px" }}>
      <Kicker text="inferCapabilitiesFromIntent() · pure function" color={COLORS.green} />
      <div style={{ width: 1640 }}>
        <PipelineGraph nodes={PIPE} startFrame={6} stepFrames={22} nodeWidth={230} />
      </div>
    </AbsoluteFill>
    <Caption lines={APPROACH} accent="green" />
  </AbsoluteFill>
);

/* 05 — CODE (the real rules + confidence) */
const TS = `// capability-inference.ts — keyword-driven, no LLM on the hot path
const KEYWORD_RULES = [
  { capability: 'gmail_send',
    keywords: ['email','mail','send','notify','digest','alert'] },
  { capability: 'kb_read',
    keywords: ['code','audit','review','docs','recent','updates'] },
  { capability: 'reasoning',
    keywords: ['audit','summarize','explain','compose','draft'] },
]

for (const rule of KEYWORD_RULES)
  for (const kw of rule.keywords)
    if (text.includes(kw)) matched.add(rule.capability)

// confidence is JUST the match count
const confidence =
  caps.length >= 3 ? 'high' : caps.length >= 1 ? 'medium' : 'low'`;
export const Scene05Code: React.FC = () => (
  <AbsoluteFill style={center}>
    <SlideTimeBar sceneDurationFrames={SCENE.code.duration} />
    <AbsoluteFill style={{ alignItems: "center", justifyContent: "center", paddingBottom: 40 }}>
      <CodePanel
        code={TS}
        language="tsx"
        filename="src/lib/agents/capability-inference.ts"
        highlightLines={[12, 13, 14, 17, 18]}
        revealFrames={320}
        fontSize={23}
        width={1280}
      />
    </AbsoluteFill>
    <Caption lines={CODE} accent="cyan" />
  </AbsoluteFill>
);

/* 06 — TIE-BREAKER (the one guarded place an LLM runs) */
export const Scene06Tiebreak: React.FC = () => {
  const frame = useCurrentFrame();
  const g1 = interpolate(frame, [20, 50], [0, 1], { extrapolateLeft: "clamp", extrapolateRight: "clamp" });
  const g2 = interpolate(frame, [60, 90], [0, 1], { extrapolateLeft: "clamp", extrapolateRight: "clamp" });
  const g3 = interpolate(frame, [100, 130], [0, 1], { extrapolateLeft: "clamp", extrapolateRight: "clamp" });
  return (
    <AbsoluteFill style={center}>
      <SlideTimeBar sceneDurationFrames={SCENE.tiebreak.duration} />
      <AbsoluteFill style={center}>
        <Kicker text="honesty · the ONE llm call" color={COLORS.amber} />
        <Big size={58}>
          An LLM tie-breaker exists —
          <br />
          but it runs behind <span style={{ color: COLORS.amber }}>three gates</span>
        </Big>
        <div style={{ display: "flex", gap: 22, marginTop: 40 }}>
          <div style={{ opacity: g1 }}>
            <Callout label="gate 1 · when" accent="amber" width={320}>
              ONLY confidence = <b style={{ color: COLORS.text }}>medium</b>, non-canonical intent
            </Callout>
          </div>
          <div style={{ opacity: g2 }}>
            <Callout label="gate 2 · bounded" accent="amber" width={320}>
              <b style={{ color: COLORS.text }}>max_tokens 200</b> · can only ADD, never override
            </Callout>
          </div>
          <div style={{ opacity: g3 }}>
            <Callout label="gate 3 · fail-closed" accent="green" width={320}>
              any error → <b style={{ color: COLORS.text }}>silent fall back</b> to the matcher
            </Callout>
          </div>
        </div>
      </AbsoluteFill>
      <Caption lines={TIEBREAK} accent="amber" />
    </AbsoluteFill>
  );
};

/* 07 — PAYOFF */
export const Scene07Payoff: React.FC = () => {
  const frame = useCurrentFrame();
  const c1 = interpolate(frame, [110, 140], [0, 1], { extrapolateLeft: "clamp", extrapolateRight: "clamp" });
  return (
    <AbsoluteFill style={center}>
      <SlideTimeBar sceneDurationFrames={SCENE.payoff.duration} />
      <AbsoluteFill style={center}>
        <Kicker text="capability slugs to clear the interview" color={COLORS.lime} />
        <div style={{ display: "flex", alignItems: "baseline", gap: 14 }}>
          <MetricCounter value={3} from={0} startFrame={10} durationFrames={40} accent="lime" fontSize={170} />
          <span style={{ fontFamily: FONTS.display, fontWeight: 700, fontSize: 56, color: COLORS.dim }}>+ slugs</span>
        </div>
        <div style={{ fontFamily: FONTS.body, fontSize: 30, color: COLORS.dim, marginTop: 14 }}>
          = high confidence → skip the multi-turn interview, <b style={{ color: COLORS.text }}>zero LLM calls</b>
        </div>
        <div style={{ display: "flex", gap: 30, marginTop: 38, opacity: c1 }}>
          <Callout label="main entry · auto-planner" accent="green" width={400}>
            runs the matcher with <b style={{ color: COLORS.text }}>no LLM at all</b>
          </Callout>
          <Callout label="worst case" accent="cyan" width={400}>
            "we asked <b style={{ color: COLORS.text }}>one extra question</b>" — tiny blast radius
          </Callout>
        </div>
      </AbsoluteFill>
      <Caption lines={PAYOFF} accent="lime" />
    </AbsoluteFill>
  );
};

/* 08 — OUTRO */
export const Scene08Outro: React.FC = () => (
  <AbsoluteFill style={center}>
    <SlideTimeBar sceneDurationFrames={SCENE.outro.duration} />
    <SceneTransition from="up">
      <AbsoluteFill style={center}>
        <Big size={64}>
          Words already carry the intent?
          <br />
          <span style={{ color: COLORS.green }}>A matcher beats an LLM call</span>
          <br />
          <span style={{ fontSize: 44, color: COLORS.dim }}>faster · cheaper · testable</span>
        </Big>
        <div style={{ fontFamily: FONTS.mono, fontSize: 24, letterSpacing: "0.24em", color: COLORS.dim, marginTop: 44 }}>
          EAKC · intent-driven missions
        </div>
      </AbsoluteFill>
    </SceneTransition>
    <Caption lines={OUTRO} accent="green" />
  </AbsoluteFill>
);

;
    var _remotion_globalVariableA;
    var _remotion_globalVariableB;
    // Legacy CSS implementations will `eval` browser code in a Node.js context
    // to extract CSS. For backwards compatibility, we need to check we're in a
    // browser context before continuing.
    if (typeof self !== 'undefined' &&
        // AMP / No-JS mode does not inject these helpers:
        '$RefreshHelpers$' in self) {
        const currentExports = __webpack_module__.exports;
        const prevExports = (_remotion_globalVariableB = (_remotion_globalVariableA = __webpack_module__.hot.data) === null || _remotion_globalVariableA === void 0 ? void 0 : _remotion_globalVariableA.prevExports) !== null && _remotion_globalVariableB !== void 0 ? _remotion_globalVariableB : null;
        // This cannot happen in MainTemplate because the exports mismatch between
        // templating and execution.
        self.$RefreshHelpers$.registerExportsForReactRefresh(currentExports, __webpack_module__.id);
        // A module can be accepted automatically based on its exports, e.g. when
        // it is a Refresh Boundary.
        if (self.$RefreshHelpers$.isReactRefreshBoundary(currentExports)) {
            // Save the previous exports on update so we can compare the boundary
            // signatures.
            __webpack_module__.hot.dispose((data) => {
                data.prevExports = currentExports;
            });
            // Unconditionally accept an update to this module, we'll check if it's
            // still a Refresh Boundary later.
            __webpack_module__.hot.accept();
            // This field is set when the previous version of this module was a
            // Refresh Boundary, letting us know we need to check for invalidation or
            // enqueue an update.
            if (prevExports !== null) {
                // A boundary can become ineligible if its exports are incompatible
                // with the previous exports.
                //
                // For example, if you add/remove/change exports, we'll want to
                // re-execute the importing modules, and force those components to
                // re-render. Similarly, if you convert a class component to a
                // function, we want to invalidate the boundary.
                if (self.$RefreshHelpers$.shouldInvalidateReactRefreshBoundary(prevExports, currentExports)) {
                    __webpack_module__.hot.invalidate();
                }
                else {
                    self.$RefreshHelpers$.scheduleUpdate();
                }
            }
        }
        else {
            // Since we just executed the code for the module, it's possible that the
            // new exports made it ineligible for being a boundary.
            // We only care about the case when we were _previously_ a boundary,
            // because we already accepted this update (accidental side effect).
            const isNoLongerABoundary = prevExports !== null;
            if (isNoLongerABoundary) {
                __webpack_module__.hot.invalidate();
            }
        }
    }
